<template>
    <div class="table-container">
        <table class="score-table">
            <thead>
                <tr>
                    <!-- 固定列 -->
                    <template v-for="(header, index) in headers.slice(0, fixedColumnCount)" :key="`fixed-${index}`">
                        <th :class="['frozen-column', `fixed-col-${index}`]">
                            {{ cellText(header) }}
                        </th>
                    </template>
                    <!-- 滚动列 -->
                    <template v-for="(header, index) in headers.slice(fixedColumnCount)" :key="`scroll-${index}`">
                        <th>{{ cellText(header) }}</th>
                    </template>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(row, rowIndex) in tableData" :key="rowIndex"
                    :class="{ 'highlight-user': userIndex.includes(rowIndex) }">
                    <!-- 固定列 -->
                    <template v-for="(cell, index) in row.slice(0, fixedColumnCount)" :key="`fixed-${index}`">
                        <td :class="['frozen-column', `fixed-col-${index}`]">
                            <slot :name="'cell-' + rowIndex + '-' + index" :row="row" :cell="cell">
                                {{ cellText(cell) }}
                            </slot>
                        </td>
                    </template>
                    <!-- 滚动列 -->
                    <template v-for="(cell, index) in row.slice(fixedColumnCount)" :key="`scroll-${index}`">
                        <td @click="handleClick(row, index + fixedColumnCount, rowIndex)">
                            <slot :name="'cell-' + rowIndex + '-' + (index + fixedColumnCount)" :row="row" :cell="cell">
                                {{ cellText(cell) }}
                            </slot>
                        </td>
                    </template>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script setup>
import { defineProps, defineEmits, computed, onMounted, onUnmounted } from 'vue';

// 定义组件的props
const props = defineProps({
    data: {
        type: Array,
        required: true
    },
    headers: {
        type: Array,
        required: true
    },
    userIndex: {
        type: Array,
        default: () => []
    },
    fixedColumnCount: {
        type: Number,
        default: 2,
        validator: (value) => value >= 0
    }
});

const cellText = function (cell) {
    return cell['text'];
};

// 定义组件的emits
// 计算属性：获取每行数据的rowScoreInfos
const tableData = computed(() => {
    return props.data.map(row => {
        const rowData = row.rowScoreInfos;
        return rowData;
    });
});

const emit = defineEmits(['cell-clicked']);

// 计算和更新固定列的宽度
const updateFixedColumnsWidth = () => {
    const table = document.querySelector('.score-table');
    if (!table) return;

    const rows = table.querySelectorAll('tr');
    if (!rows.length) return;

    // 使用第一行（表头）来计算列宽
    const firstRow = rows[0];
    const cells = firstRow.querySelectorAll('.frozen-column');

    let accumulatedWidth = 0;
    cells.forEach((cell, index) => {
        if (index < props.fixedColumnCount) {
            // 第一列不需要设置位置
            if (index > 0) {
                // 使用 offsetWidth 获取实际宽度，包含padding和border
                document.documentElement.style.setProperty(
                    `--col-${index}-position`,
                    `${accumulatedWidth}px`
                );
            }
            // 累加当前列的宽度，包含border
            const computedStyle = window.getComputedStyle(cell);
            const width = cell.offsetWidth;
            const borderRight = parseFloat(computedStyle.borderRightWidth) || 0;
            accumulatedWidth += width + borderRight;
        }
    });
};

// 监听窗口大小变化
let resizeObserver;

// 创建一个防抖的更新函数
const debouncedUpdate = (() => {
    let timer = null;
    return () => {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
            requestAnimationFrame(updateFixedColumnsWidth);
        }, 16); // 约一帧的时间
    };
})();

// 窗口调整大小的处理函数
const handleResize = () => debouncedUpdate();

// 检查表格是否可以横向滚动
const updateScrollState = () => {
    const container = document.querySelector('.table-container');
    if (!container) return;

    const hasHorizontalScroll = container.scrollWidth > container.clientWidth;
    container.setAttribute('data-scroll', hasHorizontalScroll ? '1' : '0');

    // 更新滚动阴影
    const scrollRatio = container.scrollLeft / (container.scrollWidth - container.clientWidth);
    container.style.setProperty('--scroll-ratio', scrollRatio);
};

// 创建事件处理函数的引用以便清理
const handleWindowResize = () => {
    handleResize();
    updateScrollState();
};

// 创建滚动处理函数的引用以便清理
const handleScroll = () => {
    requestAnimationFrame(updateScrollState);
};

onMounted(() => {
    // 立即执行一次计算
    updateFixedColumnsWidth();

    // 获取表格容器
    const tableContainer = document.querySelector('.table-container');

    // 在下一帧执行，确保DOM完全渲染
    requestAnimationFrame(() => {
        updateFixedColumnsWidth();
        updateScrollState();

        // 监听字体加载完成后再次更新
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(() => {
                updateFixedColumnsWidth();
                updateScrollState();
            });
        }
    });

    // 创建 ResizeObserver 实例
    resizeObserver = new ResizeObserver(() => {
        debouncedUpdate();
        updateScrollState();
    });

    // 监听表格容器大小变化和滚动
    if (tableContainer) {
        resizeObserver.observe(tableContainer);
        tableContainer.addEventListener('scroll', handleScroll, { passive: true });
    }

    // 监听窗口大小变化
    window.addEventListener('resize', handleWindowResize, { passive: true });
});

onUnmounted(() => {
    // 清理所有监听器
    if (resizeObserver) {
        resizeObserver.disconnect();
    }

    const tableContainer = document.querySelector('.table-container');
    if (tableContainer) {
        tableContainer.removeEventListener('scroll', handleScroll);
    }

    window.removeEventListener('resize', handleWindowResize);
});

// 定义事件处理函数
function handleClick(row, cellIndex, rowIndex) {
    const cellValue = row[cellIndex];
    emit('cell-clicked', {
        rowIndex: rowIndex,
        colIndex: cellIndex,
        cellValue: cellValue
    });
}
</script>

<style>
@import '../assets/styles/components/match-score-table.css';
</style>